import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, AlertTriangle } from "lucide-react";

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { signup: _unused } = useAuth();  // for storage helpers parity

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/invites/preview/${token}`);
        setPreview(r.data);
        // Apply firm theme CSS vars so the whole page (hero gradient,
        // row-hovers, accents) matches the firm's branding.
        const root = document.documentElement;
        if (r.data?.row_hover) root.style.setProperty("--ll-row-hover", r.data.row_hover);
        if (r.data?.accent) {
          root.style.setProperty("--ll-accent-soft", r.data.accent);
          root.style.setProperty("--ll-mint", r.data.accent);
        }
      } catch (err) {
        setError(err?.response?.data?.detail || "Invite not found or already used");
      } finally { setLoading(false); }
    })();
    return () => {
      // Reset vars so other public pages don't inherit this firm's palette.
      const root = document.documentElement;
      root.style.removeProperty("--ll-row-hover");
      root.style.removeProperty("--ll-accent-soft");
      root.style.removeProperty("--ll-mint");
    };
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/invites/accept", { token, password });
      // Persist auth
      localStorage.setItem("ll_token", r.data.token);
      localStorage.setItem("ll_user", JSON.stringify(r.data.user));
      localStorage.setItem("ll_tenant", JSON.stringify(r.data.tenant));
      toast.success(`Welcome to ${r.data.tenant.name}`);
      // Force full reload so AuthProvider re-reads localStorage. Clients
      // land on the portal, staff on the standard dashboard.
      window.location.href = r.data.user?.role === "client" ? "/client" : "/app/dashboard";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not accept invite");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ background: "var(--ll-bg)" }} data-testid="accept-invite-page">
      <div
        className="hidden lg:flex flex-col justify-between p-12 border-r relative ll-grain"
        style={{
          borderColor: "var(--ll-border)",
          background: preview?.brand_color || "var(--ll-ink)",
          color: "white",
        }}
        data-testid="accept-invite-hero"
      >
        <Link to="/" className="flex items-center gap-2" data-testid="accept-invite-logo">
          {preview?.logo_base64 ? (
            <img src={preview.logo_base64} alt={preview.tenant_name || "logo"} className="h-9 w-auto" style={{ maxWidth: 160 }} />
          ) : (
            <div className="w-7 h-7 grid place-items-center bg-white text-[var(--ll-ink)]"><span className="font-display font-bold text-sm">F</span></div>
          )}
          {!preview?.logo_base64 && (
            <span className="font-display font-bold text-lg">Financials Ready</span>
          )}
        </Link>
        <div>
          {preview?.logo_base64 && (
            <img
              src={preview.logo_base64}
              alt={preview.tenant_name || "logo"}
              className="mb-6 h-16 w-auto"
              style={{ maxWidth: 260 }}
              data-testid="accept-invite-hero-logo"
            />
          )}
          <div className="text-xs uppercase tracking-[0.25em] text-white/50 mb-3">You&apos;re invited</div>
          <div className="font-display font-bold text-4xl xl:text-5xl leading-tight">
            {preview ? <>Join <span className="italic" style={{ color: "var(--ll-ochre)" }}>{preview.tenant_name}</span>.</> : "Welcome to Financials Ready."}
          </div>
          <p className="mt-4 text-white/70 max-w-md">
            Set your password to access reconciled bank statements, AI-tagged transactions, and ready-to-export reports.
          </p>
        </div>
        <div className="text-xs uppercase tracking-[0.25em] text-white/50">One-time link · expires when used</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          {loading && <div className="ll-progress mb-6" />}

          {error && !loading && (
            <div className="text-center" data-testid="accept-invite-error">
              <div className="w-12 h-12 grid place-items-center mx-auto mb-4 bg-[#FEF2F2]" style={{ color: "var(--ll-error)" }}>
                <AlertTriangle size={22} />
              </div>
              <h1 className="font-display font-bold text-2xl mb-2">Invite unavailable</h1>
              <p className="text-sm text-[var(--ll-ink-soft)] mb-6">{error}</p>
              <Link to="/login" className="text-sm underline underline-offset-4">Go to sign in</Link>
            </div>
          )}

          {preview && !error && (
            <form onSubmit={onSubmit} data-testid="accept-invite-form">
              <div className="text-xs uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] mb-3">Accept invite</div>
              <h1 className="font-display font-bold text-3xl tracking-tight mb-2">Hi, {preview.name.split(" ")[0]}.</h1>
              <p className="text-sm text-[var(--ll-ink-soft)] mb-6">
                You&apos;ve been invited as <span className="font-medium uppercase tracking-widest text-xs px-1.5 py-0.5 bg-[var(--ll-mint)]">{preview.role}</span> on{" "}
                <span className="font-medium">{preview.tenant_name}</span>.
              </p>

              <div className="mb-4">
                <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Email</label>
                <div className="px-3 py-2.5 border bg-[var(--ll-bg-sidebar)] text-sm" style={{ borderColor: "var(--ll-border)" }} data-testid="accept-invite-email">
                  {preview.email}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Create password</label>
                <input
                  type="password" required minLength={6}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 border bg-white" style={{ borderColor: "var(--ll-border)" }}
                  placeholder="At least 6 characters"
                  data-testid="accept-invite-password"
                />
              </div>
              <div className="mb-6">
                <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Confirm password</label>
                <input
                  type="password" required
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="w-full px-3 py-2.5 border bg-white" style={{ borderColor: "var(--ll-border)" }}
                  placeholder="••••••••"
                  data-testid="accept-invite-confirm"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="accept-invite-submit"
                className="w-full bg-[var(--ll-ink)] text-white py-3 hover:bg-[#2D6A4F] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                <ShieldCheck size={15} /> {submitting ? "Joining…" : `Join ${preview.tenant_name}`}
                {!submitting && <ArrowRight size={15} />}
              </button>

              <div className="text-xs text-[var(--ll-ink-soft)] mt-4 text-center">
                Already have an account? <Link to="/login" className="underline underline-offset-4">Sign in instead</Link>.
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
